/**
 * Acquisti — un intervento visto dal lato ricambi.
 *
 * E' la destinazione delle righe di /acquisti: chi apre quella lista vuole
 * gestire l'acquisto, non leggere la scheda lavoro.
 *
 * Questa pagina fa quattro cose e si ferma: intestazione, lista della spesa,
 * elenco delle offerte, scoping per officina. Copertura per articolo, spunta
 * della consegna, resi e pannello costi NON stanno qui — sono il cantiere
 * pieno e arrivano dopo.
 */
import { useState } from 'react'
import {
  Card, Table, Tag, Typography, Button, Descriptions, Empty, Alert, Tooltip,
  Result, Spin,
} from 'antd'
import { ArrowLeftOutlined, PlusOutlined, ShoppingOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import RdaInterventoModal from '@/components/RdaInterventoModal'
import { TagArticoliResi } from '@/components/TagArticoliResi'
import type {
  ArticoloListaSpesa, DettaglioIntervento, OffertaIntervento,
} from '@/types/acquisti'

const { Text, Title } = Typography

const DettaglioAcquistoPage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { interventionId } = useParams<{ interventionId: string }>()
  const id = Number(interventionId)
  const [listaModaleAperta, setListaModaleAperta] = useState(false)

  const { data, isLoading, error } = useQuery<DettaglioIntervento>({
    queryKey: ['acquisti-intervento', id],
    queryFn: async () => (await axiosInstance.get(API_ENDPOINTS.ACQUISTI_INTERVENTO(id))).data,
    enabled: Number.isFinite(id),
    retry: false,
  })

  if (isLoading) {
    return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
  }

  if (error || !data) {
    // Copre sia il 403 dello scoping sia il 404: in entrambi i casi non c'e'
    // niente da mostrare, e non si dice quale dei due sia.
    return (
      <Result
        status="403"
        title="Intervento non disponibile"
        subTitle="Non è assegnato alla tua officina, oppure non esiste."
        extra={<Button onClick={() => navigate('/acquisti')}>Torna agli acquisti</Button>}
      />
    )
  }

  const i = data.intervento

  /**
   * PONTE PROVVISORIO — la creazione dell'offerta passa dalla pagina /rda.
   *
   * La modale dell'offerta oggi vive dentro PurchaseRequestsPage: 380 righe di
   * JSX in linea, legate a sei stati e a una dozzina di funzioni di quella
   * pagina. Non e' riusabile da qui.
   *
   * NON verra' estratta: la creazione dell'offerta sara' riscritta dentro
   * questo contenitore, ed e' il motivo per cui questo cantiere esiste. Quindi
   * qui non si costruiscono astrazioni destinate a sparire — solo un link con
   * due parametri, che il giorno della riscrittura si cancella.
   */
  const creaOfferta = () => {
    navigate(`/rda?nuova_offerta_intervento=${i.intervention_id}`)
  }

  const colonneOfferte = [
    { title: 'Codice', dataIndex: 'codice', width: 150,
      render: (v: string | null) => <Text style={{ fontSize: 12 }}>{v || '—'}</Text> },
    { title: 'Stato', dataIndex: 'stato', width: 200,
      render: (v: string | null, o: OffertaIntervento) => (
        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
          <Tag style={{ fontSize: 11, margin: 0 }}>{v || '—'}</Tag>
          <TagArticoliResi quanti={o.articoli_resi} compatto />
        </span>
      ) },
    { title: 'Fornitore', dataIndex: 'fornitore',
      render: (v: string | null) => (
        v ? <Text style={{ fontSize: 12, width: '100%' }} ellipsis={{ tooltip: v }}>{v}</Text>
          : <Text type="secondary">—</Text>
      ) },
    { title: 'Articoli', key: 'articoli', width: 100,
      render: (_: unknown, o: OffertaIntervento) => (
        <Text style={{ fontSize: 12 }}>{o.articoli_ricevuti} / {o.articoli_totali}</Text>
      ) },
    { title: 'Consegna prevista', key: 'consegna', width: 190,
      render: (_: unknown, o: OffertaIntervento) => {
        if (o.data_consegna_prevista) {
          return <Text style={{ fontSize: 12 }}>{dayjs(o.data_consegna_prevista).format('DD/MM/YY')}</Text>
        }
        if (o.motivo_deroga_data) {
          return (
            <Tooltip title={`Ordinata senza data di consegna: ${o.motivo_deroga_data}`}>
              <Tag color="warning" style={{
                fontSize: 11, maxWidth: '100%', display: 'inline-block',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                verticalAlign: 'bottom',
              }}>deroga: {o.motivo_deroga_data}</Tag>
            </Tooltip>
          )
        }
        return <Text type="secondary">—</Text>
      } },
    // La colonna compare solo se il server ha mandato gli importi: non e'
    // nascosta lato client, proprio non c'e' il dato.
    ...(data.importi_visibili ? [{
      title: 'Stimato', dataIndex: 'costo_stimato_totale', width: 110,
      render: (v: number | null | undefined) => (
        <Text style={{ fontSize: 12 }}>{v == null ? '—' : `${Number(v).toFixed(2)} €`}</Text>
      ),
    }] : []),
  ]

  return (
    <div style={{ padding: 16, maxWidth: 1100 }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/acquisti')}
        style={{ marginBottom: 8, paddingLeft: 0 }}
      >
        Acquisti
      </Button>

      <Title level={4} style={{ marginTop: 0 }}>
        #{i.progressivo ?? '?'} — {i.descrizione_intervento ?? 'Intervento'}
      </Title>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }}>
          <Descriptions.Item label="Scheda">{i.numero_scheda ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Veicolo">
            {[i.targa, i.veicolo].filter(Boolean).join(' · ') || '—'}
          </Descriptions.Item>
          {/* Per un'officina esterna il cliente arriva gia' nullo dal server */}
          <Descriptions.Item label="Cliente">{i.cliente ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Stato intervento">
            {i.stato_intervento ?? '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* ── Lista della spesa ───────────────────────────────────────────── */}
      <Card
        size="small"
        title={<span><ShoppingOutlined style={{ marginRight: 8 }} />Lista della spesa</span>}
        extra={
          <Button size="small" onClick={() => setListaModaleAperta(true)}>
            {data.lista_spesa.length ? 'Modifica' : 'Scrivi la lista'}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {data.lista_spesa.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              data.richiesta_id
                ? 'La richiesta esiste ma non ha articoli'
                : 'Nessuna richiesta: il tecnico non ha ancora scritto cosa serve'
            }
          />
        ) : (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {data.lista_spesa.map((a: ArticoloListaSpesa) => (
              <li key={a.id}>
                <Text style={{ fontSize: 13 }}>{a.descrizione}</Text>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ── Offerte ─────────────────────────────────────────────────────── */}
      <Card
        size="small"
        title="Offerte"
        extra={
          data.offerte.length > 0 && (
            <Button size="small" icon={<PlusOutlined />} onClick={creaOfferta}>
              Nuova offerta
            </Button>
          )
        }
      >
        {data.offerte.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Nessuna offerta per questo intervento.
            </Text>
            <Button type="primary" icon={<PlusOutlined />} onClick={creaOfferta}>
              Crea la prima offerta
            </Button>
          </div>
        ) : (
          <Table<OffertaIntervento>
            dataSource={data.offerte}
            rowKey="id"
            size="small"
            pagination={false}
            tableLayout="fixed"
            columns={colonneOfferte}
          />
        )}
      </Card>

      {!data.importi_visibili && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="Gli importi non sono visibili con i tuoi permessi"
          description="Servono costi e margine (costs.view_margin). I dati non vengono nascosti a schermo: non arrivano proprio."
        />
      )}

      {/* La lista della spesa si scrive con la modale che esiste gia', usata
          anche dentro la scheda lavoro. Nessuna copia: stesso componente. */}
      <RdaInterventoModal
        open={listaModaleAperta}
        onClose={() => {
          setListaModaleAperta(false)
          queryClient.invalidateQueries({ queryKey: ['acquisti-intervento', id] })
          queryClient.invalidateQueries({ queryKey: ['interventi-fermi'] })
        }}
        interventionId={i.intervention_id}
        workOrderId={i.work_order_id ?? 0}
        interventionInfo={{ descrizione: i.descrizione_intervento ?? '' }}
      />
    </div>
  )
}

export default DettaglioAcquistoPage
